import { useCallback, useEffect, useRef, useState } from "react";
import GraphHeader from "../../CommonComponents/GraphHeader";
import CommonLineGraph from "../../CommonComponents/CommonLineGraph";
import { useDispatch, useSelector } from "react-redux";
import { idleFunds } from "../../../../../redux/features/Settings/Statistics";
import formatTotalBalance from "../../../../../utils/formatTotalBalance";
import FecthingPage from "../../CommonComponents/FecthingPage";
import CommonToast from "../../../../_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";

const IdleFund = () => {
  // default values for the graph stored in variables
  const defaultGraphData = {
    defaultItemsSizeForXAxis: [0, 1, 2, 3, 4, 5, 6],
    dataFirst: [40, 60, 40, 70, 50, 40, 70],
    dataSecond: [0, 15, 0, 0, 0, 0, 0],
    xAxisData: [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    totalIdleFund: 5600000,
  };
  const [graphData, setGraphData] = useState(defaultGraphData);

  const [selectedDays, setSelectedDays] = useState("last 7 days");
  const [selectedAmount, setSelectedAmount] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [fetchPageEnabled, setFetchPageEnabled] = useState(true);
  const [isError, setIsError] = useState(false);
  const [rawReportData, setRawReportData] = useState(null);
  const toastShownRef = useRef(false);

  const dispatch = useDispatch();
  const { idleFundsLoading } = useSelector((state) => state.settingStatistics);

  /* Helper to transform API response into graph-friendly arrays */
  const transformReportData = (reports, selectedAmount) => {
    return reports.reduce(
      (acc, val, index) => {
        acc.defaultGraphDataItemsForXaxis.push(index);
        acc.graphDataFirst.push(
          selectedAmount
            ? formatTotalBalance(val.total_idle_funds, selectedAmount).amount
            : val.total_idle_funds,
        );
        acc.graphDataSecond.push(
          selectedAmount
            ? formatTotalBalance("0.0", selectedAmount).amount
            : "0.0",
        );
        acc.xAxisLabels.push(val.day);
        return acc;
      },
      {
        defaultGraphDataItemsForXaxis: [],
        graphDataFirst: [],
        graphDataSecond: [],
        xAxisLabels: [],
      },
    );
  };

  /* Fetch and update state with API data */
  const fetchData = useCallback(async () => {
    if (!selectedAccount) return;

    const response = await dispatch(
      idleFunds({ owner_acct_id: selectedAccount.value, period: selectedDays }),
    );

    const reportData = response?.payload?.report_data;

    if (response?.payload?.status && reportData) {
      setRawReportData(reportData);
      const {
        defaultGraphDataItemsForXaxis,
        graphDataFirst,
        graphDataSecond,
        xAxisLabels,
      } = transformReportData(reportData.idle_fund_data || [], selectedAmount);

      setGraphData({
        defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
        dataFirst: graphDataFirst,
        dataSecond: graphDataSecond,
        xAxisData: xAxisLabels,
        totalIdleFund: reportData.total_idle_fund,
      });
      // toast shows only once
      if (!toastShownRef.current && fetchPageEnabled) {
        CommonToast({
          text: "Fetched successfully.",
          icon: ICONS?.SELECTED_WITH_WHITE_FILLED,
          variant: "var(--success)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
        toastShownRef.current = true;
      }
      setFetchPageEnabled(false);
      setIsError(false);
    } else {
      // fallback to defaults
      setGraphData(defaultGraphData);
      setFetchPageEnabled(true);
      toastShownRef.current = false;

      setIsError(true);
    }
  }, [dispatch, selectedAccount, selectedDays]);

  /* Button handler */
  const handleFetchClick = () => {
    fetchData();
  };

  /*Auto-fetch on dependency change */
  useEffect(() => {
    if (!fetchPageEnabled) {
      fetchData();
    }
  }, [fetchPageEnabled, fetchData, dispatch]);

  /* Recalculate graph when only unit changes */
  useEffect(() => {
    if (rawReportData) {
      const {
        defaultGraphDataItemsForXaxis,
        graphDataFirst,
        graphDataSecond,
        xAxisLabels,
      } = transformReportData(
        rawReportData.idle_fund_data || [],
        selectedAmount,
      );

      setGraphData({
        defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
        dataFirst: graphDataFirst,
        dataSecond: graphDataSecond,
        xAxisData: xAxisLabels,
        totalIdleFund: rawReportData.total_idle_fund,
      });
    }
  }, [selectedAmount, rawReportData]);

  return (
    <div className="w-width-full flex flex-col bg-primary-card rounded-border-radius-md">
      <GraphHeader
        handlingOptionsRequired={[
          { type: "days", setterState: setSelectedDays },
          { type: "amounts", setterState: setSelectedAmount },
          { type: "favouriteAccounts", setterState: setSelectedAccount },
        ]}
        titleText={"Idle funds"}
      />
      <div className="flex flex-col relative">
        <FecthingPage
          fetchPageEnabled={fetchPageEnabled}
          loading={idleFundsLoading}
          isError={isError}
          handleFunction={handleFetchClick}
        />

        <div className="w-width-full">
          <div>
            <CommonLineGraph
              graphDataFirst={graphData.dataFirst}
              graphDataSecond={graphData.dataSecond}
              xAxisLabels={graphData.xAxisData}
              graphDataFirstColor={"var(--failed)"}
              graphDataSecondColor={"var(--text-secondary)"}
              defaultDataItemsForXaxis={graphData.defaultItemsSizeForXAxis}
              amountFormatForLegend={selectedAmount}
            />
          </div>
        </div>
        <div className="w-width-full bg-primary-nav rounded-b-border-radius-md">
          <div className="flex Qsmd:flex-row flex-col Qsmd:gap-gap-md gap-gap-sm py-padding-sm px-padding-md">
            <div className="flex gap-gap-md">
              <div className="text-text-secondary text-font-xxs">
                Average idle funds :
              </div>
              <div className="text-negative-act text-font-xs font-semibold">
                {fetchPageEnabled
                  ? "-"
                  : "INR " +
                    formatTotalBalance(
                      graphData.totalIdleFund,
                      selectedAmount === "" ? "amount" : selectedAmount,
                    ).amount +
                    " " +
                    selectedAmount}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdleFund;
